/**
 * Media Session — Lock Screen and headset transport control.
 *
 * The honest limitation, stated once here and repeated in the capability matrix: **iOS only
 * routes Now Playing to a page that is playing an `HTMLMediaElement`.** PadLab's output is a
 * Web Audio graph, so on iPhone the metadata below is registered correctly and simply never
 * appears on the Lock Screen unless the audio engine also pumps its master bus through an
 * `<audio>` element — which is what `bindMediaElement` is for. On Chromium and on desktop
 * Safari the handlers work as written.
 *
 * PadLab does not fake it. There is no hidden silent track kept playing to hold a session:
 * that steals the Now Playing slot from the user's music, burns battery, and lies about what
 * the app is doing.
 */

import { createLogger, shape } from '../core/log';

const log = createLogger('media-session');

/**
 * The transport, described structurally so this module never imports the audio engine. The
 * engine passes an object; whichever members exist become Lock Screen actions.
 */
export interface TransportBridge {
  play(): void;
  pause(): void;
  stop(): void;
  previousBank?(): void;
  nextBank?(): void;
  seekTo?(positionSeconds: number): void;
  seekBackward?(offsetSeconds: number): void;
  seekForward?(offsetSeconds: number): void;
}

export interface NowPlaying {
  /** The project or take name. */
  readonly title: string;
  /** What is playing within it — "Bank A", "Pattern 2", "Take 3". */
  readonly subtitle?: string;
  readonly album?: string;
}

export type TransportState = 'playing' | 'paused' | 'none';

interface MediaSessionLike {
  metadata: unknown;
  playbackState: TransportState;
  setActionHandler(action: string, handler: ((details: unknown) => void) | null): void;
  setPositionState?(state: { duration: number; playbackRate?: number; position?: number }): void;
}

const session: MediaSessionLike | undefined = (
  navigator as unknown as { mediaSession?: MediaSessionLike }
).mediaSession;

export const mediaSessionSupported = session !== undefined;

/** Artwork resolved against the document, so it is correct at any deployment sub-path. */
function artwork(): Array<{ src: string; sizes: string; type: string }> {
  const resolve = (path: string): string => new URL(path, document.baseURI).href;
  return [
    { src: resolve('./icons/icon-192.png'), sizes: '192x192', type: 'image/png' },
    { src: resolve('./icons/icon-512.png'), sizes: '512x512', type: 'image/png' },
  ];
}

/**
 * `setActionHandler` throws `NotSupportedError` for actions a platform does not route, and
 * the set differs per browser and per OS version. Each one is therefore installed
 * independently: losing `seekto` must not cost us `play`.
 */
function installHandler(action: string, handler: ((details: unknown) => void) | null): boolean {
  if (session === undefined) return false;
  try {
    session.setActionHandler(action, handler);
    return true;
  } catch {
    return false;
  }
}

let installedActions: string[] = [];

/**
 * Wires the transport to the system's media controls. Returns a disposer that removes every
 * handler it installed — important when the engine is rebuilt after an audio interruption,
 * because a stale handler would call into a dead graph.
 */
export function configureMediaSession(bridge: TransportBridge): () => void {
  if (session === undefined) {
    log.debug('Media Session is not implemented; transport control stays in-app only');
    return () => {};
  }

  const wired: string[] = [];
  const wire = (action: string, handler: (details: unknown) => void): void => {
    if (installHandler(action, handler)) wired.push(action);
  };

  wire('play', () => bridge.play());
  wire('pause', () => bridge.pause());
  wire('stop', () => bridge.stop());

  if (bridge.previousBank !== undefined) wire('previoustrack', () => bridge.previousBank?.());
  if (bridge.nextBank !== undefined) wire('nexttrack', () => bridge.nextBank?.());

  if (bridge.seekTo !== undefined) {
    wire('seekto', (details) => {
      const time = (details as { seekTime?: unknown }).seekTime;
      if (typeof time === 'number' && Number.isFinite(time)) bridge.seekTo?.(time);
    });
  }
  if (bridge.seekBackward !== undefined) {
    wire('seekbackward', (details) => {
      const offset = (details as { seekOffset?: unknown }).seekOffset;
      bridge.seekBackward?.(typeof offset === 'number' ? offset : 10);
    });
  }
  if (bridge.seekForward !== undefined) {
    wire('seekforward', (details) => {
      const offset = (details as { seekOffset?: unknown }).seekOffset;
      bridge.seekForward?.(typeof offset === 'number' ? offset : 10);
    });
  }

  installedActions = wired;
  log.debug('Media Session handlers installed', { actions: wired.length });

  return () => {
    for (const action of wired) installHandler(action, null);
    installedActions = [];
    if (session !== undefined) session.playbackState = 'none';
  };
}

export function setNowPlaying(info: NowPlaying): void {
  if (session === undefined) return;
  const MetadataConstructor = (globalThis as { MediaMetadata?: new (init: unknown) => unknown })
    .MediaMetadata;
  if (MetadataConstructor === undefined) return;

  try {
    session.metadata = new MetadataConstructor({
      title: info.title,
      artist: info.subtitle ?? 'PadLab',
      album: info.album ?? 'PadLab',
      artwork: artwork(),
    });
  } catch (error) {
    log.warn('Media Session metadata was rejected', { cause: shape(error) });
  }
}

export function setTransportState(state: TransportState): void {
  if (session === undefined) return;
  session.playbackState = state;
}

/**
 * Publishes the position so the Lock Screen scrubber matches the transport.
 *
 * A non-finite or negative duration makes the platform throw, and a sequencer looping a
 * one-bar pattern produces exactly those values while the tempo is being changed, so the
 * guard is not defensive decoration.
 */
export function setTransportPosition(
  durationSeconds: number,
  positionSeconds: number,
  playbackRate = 1,
): void {
  if (session?.setPositionState === undefined) return;
  if (!Number.isFinite(durationSeconds) || durationSeconds <= 0) return;
  const position = Math.min(Math.max(positionSeconds, 0), durationSeconds);
  try {
    session.setPositionState({
      duration: durationSeconds,
      position,
      playbackRate: playbackRate > 0 ? playbackRate : 1,
    });
  } catch (error) {
    log.debug('Media Session position was rejected', { cause: shape(error) });
  }
}

/**
 * The one thing that makes Now Playing appear on iOS.
 *
 * If the audio engine routes its master bus through a `MediaStreamAudioDestinationNode` into
 * an `<audio>` element — which it does when Media Session is worth having — hand that element
 * here. Its own play/pause events then keep `playbackState` truthful, including when the
 * system pauses it for a phone call. Returns a disposer.
 *
 * Calling this is optional; every other function in this module works without it.
 */
export function bindMediaElement(element: HTMLMediaElement): () => void {
  const onPlay = (): void => setTransportState('playing');
  const onPause = (): void => setTransportState('paused');
  const onEnded = (): void => setTransportState('none');

  element.addEventListener('play', onPlay);
  element.addEventListener('pause', onPause);
  element.addEventListener('ended', onEnded);

  return () => {
    element.removeEventListener('play', onPlay);
    element.removeEventListener('pause', onPause);
    element.removeEventListener('ended', onEnded);
  };
}

export interface MediaSessionDiagnostics {
  readonly supported: boolean;
  readonly installedActions: readonly string[];
  readonly detail: string;
}

export function mediaSessionDiagnostics(): MediaSessionDiagnostics {
  if (!mediaSessionSupported) {
    return {
      supported: false,
      installedActions: [],
      detail:
        'This browser does not implement Media Session. Use the transport inside PadLab; headset buttons will not reach it.',
    };
  }
  if (installedActions.length === 0) {
    return {
      supported: true,
      installedActions: [],
      detail: 'Available, but no transport is currently wired to it.',
    };
  }
  return {
    supported: true,
    installedActions,
    detail: `${installedActions.join(', ')} are routed to the system controls. On iPhone these surface on the Lock Screen only while PadLab is driving an audio element.`,
  };
}
